import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PointAdjustment, PointAdjustmentReason } from '../types';
import { listAdjustments, createAdjustment, reverseAdjustment, CreateAdjustmentInput } from '../services/pointAdjustmentsService';

interface UsePointAdjustmentsOptions {
    leagueGroupId?: string;
    season?: string;
    enabled?: boolean;
}

interface PointAdjustmentsState {
    adjustments: PointAdjustment[];
    loading: boolean;
    error: string | null;
    refresh: () => void;
    create: (input: CreateAdjustmentInput) => Promise<PointAdjustment | null>;
    reverse: (id: string) => Promise<PointAdjustment | null>;
    aggregateByTeam: Record<string, { total: number; items: PointAdjustment[] }>;
}

export const QUERY_KEY_PREFIX = 'pointAdjustments';

export const usePointAdjustments = ({ leagueGroupId, season, enabled = true }: UsePointAdjustmentsOptions): PointAdjustmentsState => {
    const queryClient = useQueryClient();

    const queryKey = [QUERY_KEY_PREFIX, { leagueGroupId, season }];

    const { data: adjustments = [], isLoading: loading, error: queryError } = useQuery({
        queryKey,
        queryFn: () => listAdjustments(leagueGroupId!, season!),
        enabled: enabled && !!leagueGroupId && !!season,
        staleTime: 5 * 60 * 1000,
    });

    const error = queryError ? (queryError as Error).message || 'Erreur chargement ajustements' : null;

    const createMutation = useMutation({
        mutationFn: createAdjustment,
        onMutate: async (input) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<PointAdjustment[]>(queryKey);
            return { previous };
        },
        onSuccess: (created) => {
            queryClient.setQueryData<PointAdjustment[]>(queryKey, (old = []) => [created, ...old]);
        },
        onError: (_err, _input, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
        },
    });

    const reverseMutation = useMutation({
        mutationFn: reverseAdjustment,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<PointAdjustment[]>(queryKey);
            return { previous };
        },
        onSuccess: (reversed) => {
            queryClient.setQueryData<PointAdjustment[]>(queryKey, (old = []) => [reversed, ...old]);
        },
        onError: (_err, _id, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
        },
    });

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey });
    };

    const create = async (input: CreateAdjustmentInput): Promise<PointAdjustment | null> => {
        try {
            return await createMutation.mutateAsync(input);
        } catch (e: any) {
            return null;
        }
    };

    const reverse = async (id: string): Promise<PointAdjustment | null> => {
        try {
            return await reverseMutation.mutateAsync(id);
        } catch (e: any) {
            return null;
        }
    };

    const aggregateByTeam = useMemo(() => {
        const acc: Record<string, { total: number; items: PointAdjustment[] }> = {};
        adjustments.forEach(a => {
            if (!acc[a.teamId]) acc[a.teamId] = { total: 0, items: [] };
            acc[a.teamId].total += a.deltaPoints;
            acc[a.teamId].items.push(a);
        });
        return acc;
    }, [adjustments]);

    return { adjustments, loading, error, refresh, create, reverse, aggregateByTeam };
};

// Utility to create a forfait adjustment (front-end helper before sending to backend)
export const buildForfaitAdjustment = (params: {
    season: string; leagueGroupId: string; teamId: string; matchId: string; delta: number; label?: string; details?: string; userName?: string; userId?: string;
}): PointAdjustment => ({
    id: 'temp-' + Math.random().toString(36).slice(2),
    season: params.season,
    leagueGroupId: params.leagueGroupId,
    teamId: params.teamId,
    matchId: params.matchId,
    reason: PointAdjustmentReason.FORFAIT,
    deltaPoints: params.delta,
    label: params.label || 'Forfait',
    details: params.details,
    source: 'user',
    createdAt: new Date().toISOString(),
    createdBy: params.userId,
    createdByName: params.userName,
});
